<script lang="ts">
  // In-Toolbar-Indikator fuer laufende Media-Apply-Jobs. Ein kleiner
  // Knopf mit Fortschrittsring; per Klick klappt rechts darunter ein
  // Popover mit Detail-Liste auf. Versteckt wenn keine Jobs aktiv.
  //
  // Sitzt visuell wie der Live-Terminal-Indikator (h-7 w-7) im Right-
  // Cluster der Toolbar-Pille -- gleicher Rhythmus, gleiche Optik.
  import { anchoredPopover } from "./anchored-popover";
  import { mediaJobs, type MediaJob } from "./media-jobs.svelte";
  import { formatBytes } from "./format";

  // alwaysShow: auf der Files-Page wird der Button auch im Idle gezeigt
  // (Einsprung in den Verschiebungs-Verlauf). Anderswo nur sichtbar
  // wenn gerade Jobs laufen, damit die Toolbar sonst aufgeraeumt
  // bleibt.
  interface Props { alwaysShow?: boolean }
  let { alwaysShow = false }: Props = $props();

  let expanded = $state(false);
  let buttonEl = $state<HTMLButtonElement | undefined>();

  const jobs = mediaJobs(); // SvelteMap, reaktiv

  // Pro Job ein Prozentwert. Wir mischen Datei-Index und within-file-
  // Prozent: ((K-1) + within/100) / T * 100. So springt der Ring nicht
  // bei "1/1" sofort auf 100, sondern faedelt sich linear durch den
  // tatsaechlichen Transfer.
  function jobPercent(j: MediaJob): number {
    if (j.phase === "settled") return 100;
    if (j.phase === "failed") return 100; // gefuellt + rot
    if (j.totalFiles <= 0) return 0;
    const completed = Math.max(0, j.currentFile - 1);
    const within = Math.max(0, Math.min(100, j.withinFilePct));
    const pct = ((completed + within / 100) / j.totalFiles) * 100;
    return Math.min(100, Math.max(0, Math.round(pct)));
  }

  // Aggregat-Prozent fuer den Indikator-Ring (mehrere Jobs: gemittelt).
  const aggregatePercent = $derived.by(() => {
    if (jobs.size === 0) return 0;
    let sum = 0;
    for (const j of jobs.values()) sum += jobPercent(j);
    return Math.round(sum / jobs.size);
  });

  // failed dominiert die Faerbung.
  const aggregateState = $derived.by(() => {
    for (const j of jobs.values()) if (j.phase === "failed") return "failed";
    return "running";
  });

  // SVG-Ring-Geometrie fuer den 28x28-Knopf. r=11 -> circumference ~ 69.1.
  const RADIUS = 11;
  const CIRC = 2 * Math.PI * RADIUS;
  const dashOffset = $derived(CIRC * (1 - aggregatePercent / 100));

  function toggle() {
    expanded = !expanded;
  }

  // Klick ausserhalb schliesst das Popover. Nur registrieren wenn
  // offen, sonst kein unnoetiger Listener.
  function onDocClick(ev: MouseEvent) {
    if (!expanded) return;
    const target = ev.target as Node | null;
    if (!target || !buttonEl) return;
    // Im Knopf oder im Popover: drinlassen.
    if (buttonEl.contains(target)) return;
    const popover = document.querySelector("[data-media-jobs-popover]");
    if (popover && popover.contains(target)) return;
    expanded = false;
  }
  $effect(() => {
    if (!expanded) return;
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  });
</script>

{#if alwaysShow || jobs.size > 0}
<div class="relative flex items-center">
    <button
      type="button"
      bind:this={buttonEl}
      onclick={toggle}
      class="hover:bg-bg-muted/40 relative flex h-7 w-7 items-center justify-center rounded-full transition {jobs.size > 0 ? 'text-fg-muted' : 'text-fg-subtle hover:text-fg-muted'}"
      aria-label={jobs.size > 0 ? `Aktive Verschiebungen (${jobs.size})` : "Verschiebungen"}
      aria-expanded={expanded}
      title={jobs.size === 0 ? "Verschiebungen" : jobs.size === 1 ? `Verschiebung läuft (${aggregatePercent}%)` : `${jobs.size} Verschiebungen`}
    >
      {#if jobs.size > 0}
        <!-- Aktiv: Fortschrittsring + ggf. Anzahl-Badge mittig -->
        <svg viewBox="0 0 28 28" width="24" height="24" class="absolute inset-0 m-auto">
          <circle cx="14" cy="14" r={RADIUS} fill="none" stroke="currentColor" stroke-width="2" class="text-bg-muted/70"/>
          <circle
            cx="14" cy="14" r={RADIUS}
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-dasharray={CIRC}
            stroke-dashoffset={dashOffset}
            transform="rotate(-90 14 14)"
            class="{aggregateState === 'failed' ? 'text-danger' : 'text-laeuft'} transition-all duration-300 ease-out"
          />
        </svg>
        {#if jobs.size > 1}
          <span class="text-fg z-10 text-[11px] font-bold tabular-nums leading-none">
            {jobs.size}
          </span>
        {/if}
      {:else}
        <!-- Idle: Move/Transfer-Symbol als Einsprung in den Verlauf;
             damit ist die Liste auch nach einem Restart erreichbar
             ohne dass gerade was laeuft. -->
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <polyline points="17 1 21 5 17 9"/>
          <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
          <polyline points="7 23 3 19 7 15"/>
          <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
        </svg>
      {/if}
    </button>

    {#if expanded}
      <!-- Popover unter dem Button. Right-aligned damit es nicht ueber
           den Toolbar-Rand hinausragt. Opaker Hintergrund (bg-bg-elev),
           damit der Inhalt darunter den Status nicht ueberlagert -- der
           Indikator ist nur waehrend des Apply sichtbar, da kann er den
           Workspace ruhig kurz ueberdecken. Am <body> statt `absolute` im
           Button: Safari beschneidet ueberstehende Nachfahren der sticky
           TopBar-Pille (s. anchored-popover.ts). -->
      <div
        use:anchoredPopover={{ anchor: buttonEl, align: "right" }}
        data-media-jobs-popover
        class="border-border bg-bg-elev z-40 w-[min(24rem,calc(100vw-1.5rem))] overflow-y-auto rounded-xl border p-2 shadow-xl"
        role="region"
        aria-label="Aktive Verschiebungen"
      >
        <div class="border-border flex items-center justify-between border-b px-2 py-1.5">
          <span class="text-fg t-eyebrow">Verschiebungen</span>
          <button
            type="button"
            onclick={toggle}
            class="text-fg-subtle hover:text-fg"
            aria-label="Einklappen"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="18 15 12 9 6 15"/>
            </svg>
          </button>
        </div>
        <div class="max-h-96 overflow-y-auto">
          {#if jobs.size === 0}
            <div class="text-fg-subtle px-2 py-6 text-center text-xs">
              Keine aktiven Verschiebungen.
            </div>
          {/if}
          {#each [...jobs.values()] as j (j.actionID)}
            {@const pct = jobPercent(j)}
            <div class="border-border/40 border-b px-2 py-2.5 last:border-b-0">
              <!-- Zeile 1: Label + Gesamt-Prozent -->
              <div class="flex items-center justify-between gap-2">
                <span class="text-fg truncate text-xs font-medium" title={j.label}>{j.label}</span>
                <span class="text-fg-subtle font-mono t-meta tabular-nums shrink-0">{pct}%</span>
              </div>
              <!-- Zeile 2: Linear-Progress fuer Gesamtstand -->
              <div class="bg-bg-muted mt-1.5 h-1 w-full overflow-hidden rounded">
                <div
                  class="h-full transition-all duration-300 ease-out {j.phase === 'failed' ? 'bg-danger' : 'bg-laeuft'}"
                  style="width: {pct}%"
                ></div>
              </div>
              <!-- Zeile 3: aktueller File + Speed/ETA -->
              {#if j.phase === "settled"}
                <div class="text-fg-subtle mt-1.5 t-meta">Fertig</div>
              {:else if j.phase === "failed"}
                <div class="text-danger mt-1.5 truncate t-meta" title={j.reason}>
                  {j.reason || "Fehler"}
                </div>
              {:else if j.totalFiles > 0 && j.currentName}
                <div class="mt-1.5 flex items-center justify-between gap-2 t-meta">
                  <span class="text-fg-muted shrink-0 tabular-nums">
                    {j.currentFile}/{j.totalFiles}
                  </span>
                  <span class="text-fg-subtle min-w-0 flex-1 truncate font-mono" title={j.currentName}>
                    {j.currentName}
                  </span>
                </div>
                <!-- Zeile 4: Within-File-Bar + Speed/ETA/Bytes -->
                <div class="bg-bg-muted/60 mt-1 h-0.5 w-full overflow-hidden rounded">
                  <div
                    class="bg-fg-subtle h-full transition-all duration-300 ease-out"
                    style="width: {j.withinFilePct}%"
                  ></div>
                </div>
                {#if j.speed || j.eta || j.bytesCur > 0}
                  <div class="text-fg-subtle mt-1 flex items-center justify-between gap-2 font-mono t-meta tabular-nums">
                    <span>
                      {j.withinFilePct}%{j.bytesCur > 0 ? ` · ${formatBytes(j.bytesCur)}` : ""}
                    </span>
                    <span class="flex items-center gap-2">
                      {#if j.speed}<span>{j.speed}</span>{/if}
                      {#if j.eta}<span class="text-fg-muted">ETA {j.eta}</span>{/if}
                    </span>
                  </div>
                {/if}
              {:else}
                <div class="text-fg-subtle mt-1.5 t-meta">Wird vorbereitet…</div>
              {/if}
            </div>
          {/each}
        </div>
      </div>
    {/if}
  </div>
{/if}
